import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import MetricRing from './components/MetricRing';
import QuickStatsCard from './components/QuickStatsCard';
import WeeklyChart from './components/WeeklyChart';
import AchievementBadges from './components/AchievementBadges';
import GoalSettingPanel from './components/GoalSettingPanel';
import QuickActionWidget from './components/QuickActionWidget';
import Icon from '../../components/AppIcon';
import OnboardingTour, { TOUR_STORAGE_KEY } from '../../components/ui/OnboardingTour';

const Dashboard = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [greeting, setGreeting] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);

  // Check if tour should be shown (triggered by onboarding completion)
  useEffect(() => {
    const tourDone = localStorage.getItem(TOUR_STORAGE_KEY + 'client_dashboard');
    const fromOnboarding = localStorage.getItem('ff_show_tour_client');
    if (!tourDone && fromOnboarding === 'true') {
      localStorage.removeItem('ff_show_tour_client');
      const t = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Mock user data with enhanced profile
  const userData = {
    name: "Sarah Johnson",
    avatar: "/assets/images/sarah_johnson_profile.png",
    memberSince: "2024-01-15",
    currentStreak: 12,
    totalPoints: 2847
  };

  // Enhanced health metrics with real-time simulation and additional parameters
  const [healthMetrics, setHealthMetrics] = useState({
    steps: { 
      current: 8547, 
      target: 10000, 
      unit: 'steps', 
      trend: 12,
      distance: 6.2,
      distanceUnit: 'km',
      activeMinutes: 45,
      caloriesBurned: 420,
      avgPace: '8:30'
    },
    calories: { 
      current: 1850, 
      target: 2200, 
      unit: 'kcal', 
      trend: -3,
      remaining: 350,
      basalRate: 1680,
      activeRate: 170,
      mealCalories: 1850,
      burnRate: 95
    },
    water: { 
      current: 6.5, 
      target: 8, 
      unit: 'glasses', 
      trend: 8,
      remaining: 1.5,
      lastIntake: '2:30 PM',
      avgIntake: 250,
      intakeUnit: 'ml',
      hydrationLevel: 81
    },
    sleep: { 
      current: 7.2, 
      target: 8, 
      unit: 'hours', 
      trend: 5,
      remaining: 0.8,
      deepSleep: 2.1,
      lightSleep: 4.6,
      remSleep: 0.5,
      sleepQuality: 82,
      bedtime: '10:45 PM',
      wakeTime: '6:00 AM'
    }
  });

  // Loading simulation
  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 1200);

    return () => clearTimeout(loadingTimer);
  }, []);

  useEffect(() => {
    // Update time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    // Set personalized greeting based on time
    const hour = new Date()?.getHours();
    if (hour < 6) setGreeting('Good night');
    else if (hour < 12) setGreeting('Good morning');
    else if (hour < 17) setGreeting('Good afternoon');
    else if (hour < 22) setGreeting('Good evening');
    else setGreeting('Good night');

    // Simulate real-time metric updates
    const metricsTimer = setInterval(() => {
      setHealthMetrics(prev => {
        const newSteps = Math.min(
          prev?.steps?.current + Math.floor(Math.random() * 25), 
          prev?.steps?.target + 500
        );
        
        return {
          ...prev,
          steps: {
            ...prev?.steps,
            current: newSteps
          }
        };
      });
    }, 45000); // Update every 45 seconds

    return () => {
      clearInterval(timer);
      clearInterval(metricsTimer);
    };
  }, []);

  const handleMetricClick = (metricType) => {
    const routeMap = {
      steps: '/activity-tracking',
      calories: '/nutrition-tracking',
      water: '/nutrition-tracking',
      sleep: '/sleep-analysis'
    };
    navigate(routeMap?.[metricType] || '/dashboard');
  };

  const handleProfileClick = () => {
    navigate('/profile-settings');
  };

  const calculatePercentage = (current, target) => {
    return Math.min(Math.round((current / target) * 100), 100);
  };

  // Enhanced metric configurations with Health.io colors
  const metricConfigs = [
    {
      key: 'steps',
      title: 'Daily Steps',
      icon: 'Footprints',
      color: '#52C41A',
      bgColor: 'wellness-green'
    },
    {
      key: 'calories',
      title: 'Calories Burned',
      icon: 'Flame',
      color: '#ED8936',
      bgColor: 'wellness-orange'
    },
    {
      key: 'water',
      title: 'Water Intake',
      icon: 'Droplets',
      color: '#4299E1',
      bgColor: 'wellness-blue'
    },
    {
      key: 'sleep',
      title: 'Sleep Quality',
      icon: 'Moon',
      color: '#9F7AEA',
      bgColor: 'wellness-purple'
    }
  ];

  // Get wellness score based on metrics
  const getWellnessScore = () => {
    const scores = metricConfigs?.map(config => {
      const metric = healthMetrics?.[config?.key];
      return calculatePercentage(metric?.current, metric?.target);
    });
    return Math.round(scores?.reduce((sum, score) => sum + score, 0) / scores?.length);
  };

  const wellnessScore = getWellnessScore();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0B' }}>
        <div className="text-center space-y-4">
          <motion.div
            className="w-16 h-16 border-4 rounded-full mx-auto"
            style={{ borderColor: 'rgba(255,107,0,0.2)', borderTopColor: '#FF6B00' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <div className="space-y-2">
            <h2 className="text-xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Loading your wellness data...</h2>
            <p className="text-sm" style={{ color: '#6B7280' }}>Preparing your personalized health insights</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen wellness-smooth-scroll" style={{ background: '#0A0A0B' }}>
      <Header />
      <main className="wellness-container py-8">
        {/* Welcome Section - Mesh Gradient */}
        <motion.section
          data-tour="client-hero"
          className="rounded-2xl relative overflow-hidden mb-8 p-6 lg:p-8"
          style={{
            background: 'radial-gradient(ellipse at 20% 50%, rgba(0,212,255,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(170,255,0,0.06) 0%, transparent 50%), radial-gradient(ellipse at 60% 80%, rgba(255,107,0,0.06) 0%, transparent 50%), rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(20px)'
          }}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="relative z-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="space-y-4 flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <h1 className="text-3xl font-bold tracking-tight min-w-0" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>
                    <span className="text-constrained">{greeting}, {userData?.name?.split(' ')?.[0]}! 👋</span>
                  </h1>
                  <div className="px-3 py-1 rounded-full text-sm font-medium flex-shrink-0" style={{ background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.25)' }}>
                    {userData?.currentStreak} day streak! 🔥
                  </div>
                </div>
                <p className="font-medium" style={{ color: '#6B7280' }}>
                  {currentTime?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)', color: '#E8EAED' }}>
                    <Icon name="Target" size={16} />
                    <span className="whitespace-nowrap">Wellness: <span style={{ color: '#AAFF00', fontFamily: "'Inter Tight', sans-serif", fontWeight: 700 }}>{wellnessScore}%</span></span>
                  </div>
                  <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)', color: '#E8EAED' }}>
                    <Icon name="Award" size={16} />
                    <span className="whitespace-nowrap"><span style={{ color: '#FF6B00', fontFamily: "'Inter Tight', sans-serif", fontWeight: 700 }}>{userData?.totalPoints?.toLocaleString()}</span> pts</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 mt-4 lg:mt-0 flex-shrink-0">
                <div className="text-right hidden sm:block" style={{ color: '#6B7280' }}>
                  <p className="text-sm">Member since</p>
                  <p className="font-semibold" style={{ color: '#E8EAED' }}>{new Date(userData.memberSince)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</p>
                </div>
                <motion.button
                  className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
                  style={{ border: '2px solid rgba(255,107,0,0.4)', boxShadow: '0 0 16px rgba(255,107,0,0.2)' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleProfileClick}
                >
                  <img src={userData?.avatar} alt={userData?.name} className="w-full h-full object-cover pointer-events-none" />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Health Metrics Grid */}
        <motion.section data-tour="client-metrics" className="wellness-grid wellness-section mb-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
          {metricConfigs?.map((config, index) => {
            const metric = healthMetrics?.[config?.key];
            const percentage = calculatePercentage(metric?.current, metric?.target);
            return (
              <motion.div key={config?.key} className="wellness-card-metric relative" initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: index * 0.15 + 0.3, duration: 0.5, ease: [0.4, 0, 0.2, 1] }}>
                <MetricRing title={config?.title} value={metric?.current} target={metric?.target} unit={metric?.unit} icon={config?.icon} color={config?.color} bgColor={config?.bgColor} percentage={percentage} trend={metric?.trend} isGoalAchieved={percentage >= 100} onClick={() => handleMetricClick(config?.key)} />
              </motion.div>
            );
          })}
        </motion.section>

        {/* Secondary Content Grid */}
        <section className="grid grid-cols-1 lg:grid-cols-3 wellness-gap-lg wellness-section mb-10">
          <motion.div data-tour="client-weekly-chart" className="lg:col-span-2 wellness-card-chart wellness-content-container" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
            <WeeklyChart />
          </motion.div>
          <motion.div data-tour="client-quick-stats" className="wellness-card-stats wellness-content-container" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.5 }}>
            <QuickStatsCard />
          </motion.div>
        </section>

        {/* Bottom Content Grid */}
        <section className="grid grid-cols-1 lg:grid-cols-2 wellness-gap-lg wellness-section mb-10">
          <motion.div data-tour="client-achievements" className="achievements-container wellness-content-container relative" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }}>
            <AchievementBadges />
          </motion.div>
          <motion.div data-tour="client-quick-actions" className="quick-actions-container wellness-content-container relative z-elevated" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7 }}>
            <QuickActionWidget />
          </motion.div>
        </section>

        {/* Motivational Section */}
        <motion.section className="wellness-card wellness-spacing-lg text-center relative overflow-hidden mb-8" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 }}>
          <div className="relative z-10 space-y-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto" style={{ background: 'rgba(170,255,0,0.1)' }}>
              <Icon name="Heart" size={24} color="#AAFF00" />
            </div>
            <div className="space-y-3">
              <h3 className="wellness-title-secondary text-center">"Every step towards better health is a victory worth celebrating."</h3>
              <p className="wellness-text-secondary text-center max-w-2xl mx-auto">Your wellness journey is unique and valuable. Keep moving forward! 💚</p>
            </div>
            <div className="wellness-grid-3 pt-8">
              {[
                { icon: 'Activity', color: '#AAFF00', title: 'Stay Active', desc: 'Aim for 150 minutes of moderate activity weekly' },
                { icon: 'Droplets', color: '#00D4FF', title: 'Stay Hydrated', desc: 'Drink water consistently throughout the day' },
                { icon: 'Moon', color: '#FF6B00', title: 'Quality Sleep', desc: 'Maintain consistent sleep schedule for recovery' }
              ]?.map((tip) => (
                <div key={tip?.title} className="text-center space-y-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto" style={{ background: `${tip?.color}15` }}>
                    <Icon name={tip?.icon} size={20} color={tip?.color} />
                  </div>
                  <h4 className="text-sm font-semibold" style={{ color: '#E8EAED' }}>{tip?.title}</h4>
                  <p className="text-xs" style={{ color: '#6B7280' }}>{tip?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>
      </main>
      <div className="fixed bottom-6 right-6 z-elevated space-y-4">
        <QuickActionButton />
      </div>
      <GoalSettingPanel />
      {/* Onboarding Tour */}
      {showTour && (
        <OnboardingTour
          tourKey="client_dashboard"
          steps={[
            {
              target: '[data-tour="client-hero"]',
              title: 'Welcome to Your Dashboard',
              description: 'This is your personal wellness hub. See your greeting, current streak, wellness score, and today\'s date at a glance.',
              icon: 'LayoutDashboard',
              position: 'bottom',
            },
            {
              target: '[data-tour="client-metrics"]',
              title: 'Your Health Metrics',
              description: 'Track steps, calories, water intake, and sleep quality in real time. Tap any ring to dive deeper into that metric.',
              icon: 'Activity',
              position: 'bottom',
            },
            {
              target: '[data-tour="client-weekly-chart"]',
              title: 'Weekly Progress Chart',
              description: 'Visualise your activity trends over the past week. Spot patterns and see how consistent you\'ve been with your goals.',
              icon: 'BarChart2',
              position: 'top',
            },
            {
              target: '[data-tour="client-quick-stats"]',
              title: 'Quick Stats',
              description: 'A snapshot of your key performance numbers — calories, steps, and more — compared to your personal targets.',
              icon: 'TrendingUp',
              position: 'top',
            },
            {
              target: '[data-tour="client-achievements"]',
              title: 'Achievement Badges',
              description: 'Earn badges as you hit milestones. Streaks, personal bests, and consistency goals are all tracked here.',
              icon: 'Award',
              position: 'top',
            },
            {
              target: '[data-tour="client-quick-actions"]',
              title: 'Quick Actions',
              description: 'Log a workout, add a meal, or record water intake in seconds. Your most-used actions are always one tap away.',
              icon: 'Zap',
              position: 'top',
            },
          ]}
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;